/**
 * <SwipeActions /> — iter-169zr · Mobile swipe-to-reveal actions.
 *
 * Wraps a list row and exposes contextual actions (typically Editar /
 * Eliminar) revealed by a horizontal swipe-left. On desktop the wrapper
 * is a pass-through — the inline buttons keep working. The component
 * is intentionally dependency-free (no react-spring / framer-motion):
 * pointer events + CSS transforms cover the use case cleanly.
 *
 * Usage:
 *   <SwipeActions
 *     actions={[
 *       { label: "Editar", icon: Pencil, color: "bg-sky-500", onClick: ... },
 *       { label: "Eliminar", icon: Trash2, color: "bg-rose-500", onClick: ... },
 *     ]}
 *     testid="recv-row-swipe"
 *   >
 *     <Card>...</Card>
 *   </SwipeActions>
 *
 * Behaviour:
 *   • Drag from the row toward the left to reveal actions stacked on
 *     the right edge. Threshold = 40% of the panel width snaps open.
 *   • Tap the row (no drag) or tap outside snaps it closed.
 *   • A small chevron hint shows on first render so the user discovers
 *     the gesture without onboarding copy.
 *   • Disabled (pass-through) when no `actions` provided or when
 *     `window.matchMedia('(min-width: 640px)')` matches (desktop).
 */
import { useEffect, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";

const ACTION_WIDTH_PX = 76; // per action panel; matches `w-[76px]`

export function SwipeActions({ actions = [], testid, children }) {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia("(min-width: 640px)").matches,
  );
  const [offset, setOffset] = useState(0); // current x translation (px)
  const [open, setOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef(null);
  const startedOpenRef = useRef(false);
  const rootRef = useRef(null);

  // Track viewport so we can pass through on desktop without unmounting
  // children every resize.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(min-width: 640px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Close on outside tap so the row doesn't get stuck open.
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOffset(0);
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointer);
    return () => document.removeEventListener("pointerdown", onDocPointer);
  }, [open]);

  if (isDesktop || actions.length === 0) {
    // Pass-through on desktop / when no actions provided. The wrapper
    // div keeps testid attached for parity in e2e selectors.
    return <div data-testid={testid}>{children}</div>;
  }

  const panelTotal = ACTION_WIDTH_PX * actions.length;

  const onTouchStart = (e) => {
    startXRef.current = e.touches[0].clientX;
    startedOpenRef.current = open;
  };
  const onTouchMove = (e) => {
    if (startXRef.current == null) return;
    const dx = e.touches[0].clientX - startXRef.current;
    const base = startedOpenRef.current ? -panelTotal : 0;
    const next = Math.max(-panelTotal, Math.min(0, base + dx));
    setOffset(next);
  };
  const onTouchEnd = () => {
    const snapOpen = offset < -panelTotal * 0.4;
    setOffset(snapOpen ? -panelTotal : 0);
    setOpen(snapOpen);
    startXRef.current = null;
  };

  return (
    <div
      ref={rootRef}
      data-testid={testid}
      className="relative overflow-hidden touch-pan-y"
    >
      {/* Action panel — sits underneath, revealed on swipe. */}
      <div
        className="absolute inset-y-0 right-0 flex items-stretch"
        data-testid={`${testid}-actions`}
        aria-hidden={!open}
      >
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <button /* design-system: ALLOW legacy-todo */
              key={a.label}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                a.onClick?.();
                setOffset(0);
                setOpen(false);
              }}
              data-testid={a.testid || `${testid}-action-${a.label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center gap-1 text-white text-[11px] font-semibold w-[76px] ${a.color || "bg-slate-700"} active:opacity-90`}
            >
              {Icon ? <Icon className="h-5 w-5" /> : null}
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Foreground row — translates left as the user swipes. */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translateX(${offset}px)`,
          transition: isDragging ? "none" : "transform 200ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        className="relative bg-white"
      >
        {children}
        {/* Discoverability hint — fades when the row opens. */}
        {!open && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-300"
          >
            <ChevronLeft className="h-4 w-4" />
          </span>
        )}
      </div>
    </div>
  );
}
